import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import { Badge } from "../ui/badge";
import { hasFeature } from "../../lib/features";
import { sectionKeyForPath } from "../../lib/permissions";
import { navItemActive, navPath } from "../../lib/navActive";
import { navSlug } from "./navSections";

const OPEN_KEY = "fixli_nav_open";
const readOpen = () => { try { return JSON.parse(localStorage.getItem(OPEN_KEY) || "{}"); } catch { return {}; } };

const BADGE_FOR = { Notifications: "unreadCount", Messages: "vendorMsgUnread", Expenses: "expensesPending", "Purchase Orders": "posDrafts" };

function CountBadge({ n, testid }) {
  if (!n) return null;
  return <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center text-[10px] px-1" data-testid={testid}>{n}</Badge>;
}

/**
 * Shared sidebar / mobile-drawer renderer for NAV_SECTIONS. Flat sections are plain links; labelled sections are
 * collapsible groups (unless `collapsible` is false or the section is `flat`). The group holding the current page
 * opens itself; open/closed choices persist per browser. In the icon rail (`collapsed`) a group shows one icon that
 * re-expands the sidebar and opens the group.
 */
export default function NavTree({ sections, user, location, collapsed = false, collapsible = true, counts = {}, isMainClient = false, sectionAllowed = () => true, onNavigate, onExpandSidebar, testidPrefix = "nav" }) {
  const [open, setOpen] = useState(readOpen);
  const role = user?.role;

  const itemVisible = (it, s) => !!it.heading || (it.roles.includes(role) && hasFeature(user, it.feature || s?.feature) && (!it.platformAdminOnly || user?.is_platform_admin) &&
    (!it.opsDirectorOnly || user?.operations_director) && (!it.mainClientOnly || isMainClient) && sectionAllowed(sectionKeyForPath(navPath(it.to))));

  const tree = useMemo(() => sections.map((s) => {
    if (s.roles && !s.roles.includes(role) && !(s.to && s.items.some((it) => !it.heading && it.roles.includes(role)))) return null;
    if (s.platformAdminOnly && !user?.is_platform_admin) return null;
    const raw = s.items.filter((it) => itemVisible(it, s));
    // drop headings with nothing after them
    const items = raw.filter((it, i) => !it.heading || raw.slice(i + 1).some((x) => !x.heading));
    const headerLink = s.to && s.roles?.includes(role) && hasFeature(user, s.feature) && sectionAllowed(sectionKeyForPath(navPath(s.to))) ? { to: s.to, exact: s.exact, noQuery: s.noQuery, activePrefix: s.activePrefix } : null;
    if (!items.some((it) => !it.heading) && !headerLink) return null;
    return { ...s, items, headerLink };
  }).filter(Boolean), [sections, role, user, isMainClient, sectionAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // a labelled group collapses only when it has something to hide (2+ children, or a header link plus children)
  const isGroup = (s) => !!s.label && !s.flat && collapsible && (s.items.filter((it) => !it.heading).length > 1 || !!s.headerLink);
  const activeIn = (s) => s.items.some((it) => !it.heading && navItemActive(it, location)) || (s.headerLink && navItemActive(s.headerLink, location));

  // the group that holds the current page opens itself (only when the page changes, so a manual close sticks),
  // then the active link is scrolled into view once the group has expanded
  const rootRef = useRef(null);
  const locKey = location.pathname + location.search;
  useEffect(() => {
    const hit = tree.find((s) => isGroup(s) && activeIn(s));
    if (hit) setOpen((o) => (o[hit.id] ? o : { ...o, [hit.id]: true }));
    const t = setTimeout(() => rootRef.current?.querySelector(".nav-link-smooth.active")?.scrollIntoView({ block: "nearest" }), 450);
    return () => clearTimeout(t);
  }, [locKey, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setOpen((o) => { const next = { ...o, [id]: !o[id] }; try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch {} return next; });
  const openGroup = (id) => setOpen((o) => { const next = { ...o, [id]: true }; try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch {} return next; });

  const renderItem = (s, it, nested) => {
    if (it.heading) {
      return <div key={`${s.id}-h-${it.heading}`} className={`${nested ? "pl-9" : "px-3"} pt-3 pb-1 text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/50`} data-testid={`${testidPrefix}-${s.id}-heading-${navSlug(it.heading)}`}>{it.heading}</div>;
    }
    const active = navItemActive(it, location);
    const Icon = it.icon;
    const n = counts[BADGE_FOR[it.label]] || 0;
    const slug = navSlug(it.label);
    const grouped = !!s.label && !s.flat && it.to !== s.to; // stable test ids whichever menu mode is on
    return (
      <Link key={`${s.id}-${it.to}`} to={it.to} onClick={onNavigate} title={collapsed ? it.label : undefined}
        data-testid={grouped ? `${testidPrefix}-${s.id}-${slug}` : `${testidPrefix}-${slug}`}
        className={`flex items-center gap-3 rounded-md text-sm font-medium nav-link-smooth ${nested && !collapsed ? "pl-9 pr-3 py-1.5" : "px-3 py-2"}
          ${active ? "bg-primary text-primary-foreground active" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
        <Icon size={nested && !collapsed ? 16 : 20} weight={active ? "fill" : "regular"} />
        {!collapsed && <span className="flex-1 truncate">{it.label}</span>}
        {!collapsed && <CountBadge n={n} testid={n ? `${testidPrefix}-badge-${slug}` : undefined} />}
      </Link>
    );
  };

  return (
    <div ref={rootRef} className="contents">
      {tree.map((s) => {
        if (!isGroup(s)) {
          const nested = !!s.headerLink && !collapsed;
          return (
            <div key={s.id}>
              {s.label && !s.headerLink && !collapsed && (
                <div className="flex items-center gap-2 px-3 pt-5 pb-1.5" data-testid={`${testidPrefix}-section-${s.id}`}>
                  <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/50">{s.label}</span>
                  <div className="flex-1 h-px bg-border/40" />
                </div>
              )}
              {s.label && !s.headerLink && collapsed && <div className="h-px bg-border/40 mx-2 my-3" />}
              {s.headerLink && renderItem(s, { to: s.to, label: s.label, icon: s.icon, exact: s.exact, noQuery: s.noQuery, activePrefix: s.activePrefix }, false)}
              {s.items.map((it) => renderItem(s, it, nested))}
            </div>
          );
        }
        const Icon = s.icon;
        const active = activeIn(s);
        const isOpen = !!open[s.id];
        const total = s.items.reduce((sum, it) => sum + (it.heading ? 0 : counts[BADGE_FOR[it.label]] || 0), 0);
        const header = s.headerLink ? { to: s.to, label: s.label, icon: s.icon, exact: s.exact, noQuery: s.noQuery, activePrefix: s.activePrefix } : null;
        const headerActive = header ? navItemActive(header, location) : false;
        if (collapsed) {
          return (
            <button key={s.id} type="button" title={total ? `${s.label} · ${total}` : s.label} onClick={() => { onExpandSidebar && onExpandSidebar(); openGroup(s.id); }} data-testid={`${testidPrefix}-group-${s.id}`}
              className={`relative flex items-center justify-center w-full px-3 py-2 rounded-md nav-link-smooth ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <Icon size={20} weight={active ? "fill" : "regular"} />
              {total > 0 && <span className="absolute top-1.5 right-2.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-sidebar" data-testid={`${testidPrefix}-group-dot-${s.id}`} />}
            </button>
          );
        }
        return (
          <div key={s.id} className="mt-0.5" data-testid={`${testidPrefix}-section-${s.id}`}>
            <div className={`flex items-center rounded-md text-sm font-medium nav-link-smooth ${headerActive ? "bg-primary text-primary-foreground" : active && !isOpen ? "text-foreground bg-muted/60" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              {header ? (
                <Link to={header.to} onClick={onNavigate} className="flex items-center gap-3 flex-1 min-w-0 px-3 py-2" data-testid={`${testidPrefix}-${navSlug(s.label)}`}>
                  <Icon size={20} weight={headerActive || active ? "fill" : "regular"} />
                  <span className="flex-1 truncate">{s.label}</span>
                </Link>
              ) : (
                <button type="button" onClick={() => toggle(s.id)} className="flex items-center gap-3 flex-1 min-w-0 px-3 py-2 text-left" data-testid={`${testidPrefix}-group-${s.id}`} aria-expanded={isOpen}>
                  <Icon size={20} weight={active ? "fill" : "regular"} />
                  <span className="flex-1 truncate">{s.label}</span>
                </button>
              )}
              {!isOpen && <CountBadge n={total} testid={total ? `${testidPrefix}-group-badge-${s.id}` : undefined} />}
              <button type="button" onClick={() => toggle(s.id)} aria-label={`${isOpen ? "Collapse" : "Expand"} ${s.label}`} aria-expanded={isOpen}
                data-testid={header ? `${testidPrefix}-group-${s.id}` : `${testidPrefix}-group-caret-${s.id}`}
                className={`shrink-0 p-2 mr-1 rounded-md ${headerActive ? "hover:bg-primary-foreground/15" : "hover:bg-muted"}`}>
                <CaretDown size={14} className={`transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
              </button>
            </div>
            <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`} data-testid={`${testidPrefix}-group-items-${s.id}`} data-open={isOpen ? "true" : "false"} inert={!isOpen}>
              <div className="overflow-hidden">
                <div className="flex flex-col gap-0.5 pb-1">{s.items.map((it) => renderItem(s, it, true))}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
